import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import Alert from '@mui/joy/Alert';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import IconButton from '@mui/joy/IconButton';
import { Paperclip, DownloadSimple } from '@phosphor-icons/react';
import type { EmailBody, EmailAttachmentMeta } from '../../types/inbox';
import api, { downloadApiBlob, fetchApiBlob } from '../../lib/api';

interface EmailBodyViewerProps {
  messageId: number;
}

const formatSize = (bytes: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Types the browser can render natively — clicking these previews instead of
// downloading. Everything else falls back to a download.
const isPreviewable = (contentType: string): boolean =>
  contentType.startsWith('image/') || contentType === 'application/pdf';

/**
 * Fetches and renders one email's body on demand (the backend pulls it from
 * Gmail/Microsoft Graph on first view and caches it, so re-opens are instant).
 * HTML renders inside a sandboxed iframe with scripts blocked.
 *
 * Inline images: the API authenticates with a JWT header (no cookies), so
 * <img src="/api/..."> inside the iframe would 401 — instead, cid: references
 * are resolved by fetching the matching inline attachment as an authenticated
 * blob and substituting an object URL (CSP img-src allows blob:).
 *
 * Attachments: images and PDFs open in a preview (modal / new tab) without
 * downloading; other types download. Nothing is ever stored on our side.
 */
export const EmailBodyViewer: React.FC<EmailBodyViewerProps> = ({ messageId }) => {
  const [body, setBody] = useState<EmailBody | null>(null);
  const [resolvedHtml, setResolvedHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyAttachment, setBusyAttachment] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ url: string; filename: string } | null>(null);
  const [frameHeight, setFrameHeight] = useState(120);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const objectUrls = useRef<string[]>([]);

  const fetchAttachmentBlob = (attachmentId: string): Promise<Blob> =>
    fetchApiBlob(
      `/api/inbox/email-messages/${messageId}/attachments/${encodeURIComponent(attachmentId)}/`,
    );

  const trackObjectUrl = (blob: Blob): string => {
    const url = URL.createObjectURL(blob);
    objectUrls.current.push(url);
    return url;
  };

  /** Replace <img src="cid:..."> with authenticated blob object URLs. */
  const resolveInlineImages = async (
    rawHtml: string,
    attachments: EmailAttachmentMeta[],
  ): Promise<string> => {
    const cidRefs = new Set<string>();
    const pattern = /src=["']cid:([^"']+)["']/gi;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(rawHtml)) !== null) cidRefs.add(match[1]);
    if (cidRefs.size === 0) return rawHtml;

    const byContentId = new Map<string, EmailAttachmentMeta>();
    for (const attachment of attachments) {
      if (attachment.content_id) {
        byContentId.set(attachment.content_id.toLowerCase(), attachment);
      }
    }

    let html = rawHtml;
    for (const cid of cidRefs) {
      let key = cid;
      try {
        key = decodeURIComponent(cid);
      } catch {
        // keep the raw cid if it isn't valid URI encoding
      }
      const attachment = byContentId.get(key.toLowerCase());
      if (!attachment) continue;
      try {
        const blob = await fetchAttachmentBlob(attachment.attachment_id);
        html = html.split(`cid:${cid}`).join(trackObjectUrl(blob));
      } catch {
        // leave this image unresolved rather than failing the whole body
      }
    }
    return html;
  };

  const loadBody = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<EmailBody>(`/api/inbox/email-messages/${messageId}/body/`);
      setBody(response.data);
      setResolvedHtml(
        response.data.body_html
          ? await resolveInlineImages(response.data.body_html, response.data.attachments || [])
          : '',
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not load this email.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBody();
    return () => {
      objectUrls.current.forEach(URL.revokeObjectURL);
      objectUrls.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId]);

  const handleFrameLoad = () => {
    const doc = frameRef.current?.contentDocument;
    if (doc?.body) {
      const height = Math.min(Math.max(doc.body.scrollHeight + 24, 60), 600);
      setFrameHeight(height);
    }
  };

  const handleOpen = async (attachment: EmailAttachmentMeta) => {
    if (!isPreviewable(attachment.content_type)) {
      return handleDownload(attachment);
    }
    setBusyAttachment(attachment.attachment_id);
    try {
      const blob = await fetchAttachmentBlob(attachment.attachment_id);
      const url = trackObjectUrl(blob);
      if (attachment.content_type.startsWith('image/')) {
        setPreview({ url, filename: attachment.filename });
      } else {
        // PDF: a top-level blob: tab uses the browser's native viewer
        window.open(url, '_blank', 'noopener');
      }
    } catch (err: any) {
      setError(err?.message || 'Preview failed');
    } finally {
      setBusyAttachment(null);
    }
  };

  const handleDownload = async (attachment: EmailAttachmentMeta) => {
    setBusyAttachment(attachment.attachment_id);
    try {
      await downloadApiBlob(
        `/api/inbox/email-messages/${messageId}/attachments/${encodeURIComponent(attachment.attachment_id)}/`,
        attachment.filename || 'attachment',
      );
    } catch (err: any) {
      setError(err?.message || 'Download failed');
    } finally {
      setBusyAttachment(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size="sm" />
      </Box>
    );
  }

  if (error && !body) {
    return (
      <Alert color="warning" variant="soft" size="sm" sx={{ my: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Typography level="body-sm">{error}</Typography>
          <Button size="sm" variant="outlined" color="primary" onClick={loadBody}>
            Retry
          </Button>
        </Stack>
      </Alert>
    );
  }

  if (!body) return null;

  // <base target="_blank"> so links open in a new tab (the sandbox blocks
  // navigation inside the frame); scripts stay blocked — no allow-scripts.
  const srcDoc = resolvedHtml
    ? `<!doctype html><html><head><base target="_blank">
       <style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
       font-size:14px;color:#171A1C;margin:12px;word-break:break-word;}
       img{max-width:100%;height:auto;}</style></head><body>${resolvedHtml}</body></html>`
    : '';

  return (
    <Stack spacing={1}>
      {error && (
        <Alert color="warning" variant="soft" size="sm">
          <Typography level="body-xs">{error}</Typography>
        </Alert>
      )}
      {resolvedHtml ? (
        <Box
          component="iframe"
          ref={frameRef}
          srcDoc={srcDoc}
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="no-referrer"
          onLoad={handleFrameLoad}
          title="Email content"
          sx={{
            width: '100%',
            height: frameHeight,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 'sm',
            bgcolor: '#fff',
          }}
        />
      ) : (
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'background.level1',
            borderRadius: 'sm',
            maxHeight: 600,
            overflowY: 'auto',
          }}
        >
          <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
            {body.body_text || '(no content)'}
          </Typography>
        </Box>
      )}

      {body.attachments.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {body.attachments.filter(a => !a.is_inline).map(attachment => (
            <Chip
              key={attachment.attachment_id || attachment.filename}
              size="sm"
              variant="outlined"
              color="neutral"
              startDecorator={
                busyAttachment === attachment.attachment_id
                  ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '14px' }} />
                  : <Paperclip size={14} />
              }
              endDecorator={
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  aria-label={`Download ${attachment.filename}`}
                  onClick={event => {
                    event.stopPropagation();
                    handleDownload(attachment);
                  }}
                  sx={{ '--IconButton-size': '18px', minWidth: 18, minHeight: 18 }}
                >
                  <DownloadSimple size={14} />
                </IconButton>
              }
              onClick={() => handleOpen(attachment)}
              disabled={busyAttachment !== null}
            >
              {attachment.filename}{attachment.size ? ` (${formatSize(attachment.size)})` : ''}
            </Chip>
          ))}
        </Stack>
      )}

      <Modal open={preview !== null} onClose={() => setPreview(null)}>
        <ModalDialog
          layout="center"
          sx={{ p: 2, maxWidth: '92vw', maxHeight: '92vh', overflow: 'auto' }}
        >
          <ModalClose />
          <Typography level="title-sm" sx={{ pr: 4 }}>{preview?.filename}</Typography>
          {preview && (
            <Box
              component="img"
              src={preview.url}
              alt={preview.filename}
              sx={{ maxWidth: '86vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 'sm' }}
            />
          )}
        </ModalDialog>
      </Modal>
    </Stack>
  );
};

export default EmailBodyViewer;
